import axios, {AxiosInstance} from 'axios'
import MyAxiosInstanceAnalyzer from './axiosInstanceAnalyzer'
import {IResponsePresignedUrl} from 'types/preSigned'
import type {Regime} from '../constants/documentTypes'

const DATABASE_SLUG: Record<Regime, string> = {
    '14133': 'upload-12-01-26',
    '13303': 'licito-estatais',
    '14903': 'smcultura-2026-08-14',
}

const PROMPT: Record<Regime, string> = {
    '14133':
        'Você é um analisador de Documentos, especialista em licitações regidas pela Lei nº 14.133/2021.',
    '13303':
        'Você é um analisador de Documentos, especialista em licitações e contratos de empresas estatais regidos pela Lei nº 13.303/2016 (Lei das Estatais).',
    '14903':
        'Você é um analisador de Documentos, especialista em fomento cultural regido pela Lei nº 14.903/2024 (Marco Legal da Cultura).',
}

export class AnalyzerApiService {
    private analizerApi: AxiosInstance

    client = axios.create({
        baseURL: import.meta.env.VITE_ANALYZER_URL,
    })

    constructor() {
        this.analizerApi = MyAxiosInstanceAnalyzer
    }

    async analyzeProject(
        file: File,
        document_type_id: string,
        regime: Regime = '14133',
        // LCTO-1920: base própria do tipo (ex.: fomento cultural da SMC)
        databaseSlug?: string
    ): Promise<any> {
        const payload = {
            document_type_id: document_type_id,
            // Regime jurídico da análise. O backend usa este campo para escolher
            // a persona/prompt da IA e a base de conhecimento (Pinecone) corretas.
            database_slug: databaseSlug ?? DATABASE_SLUG[regime],
            doc_analysis_name: file.name,
            file_url: file.name,
            content_type: file.type,
            prompt: PROMPT[regime],
        }

        const response: {data: IResponsePresignedUrl} =
            await this.analizerApi.post('/document-analysis', payload)

        const uploadData: IResponsePresignedUrl = response.data

        if (!uploadData.file_url) {
            throw new Error(
                'Signed upload URL not provided by analyzer service.'
            )
        }

        console.log('Presigned URL obtida:', uploadData.file_url)

        await axios.put(uploadData.file_url, file, {
            headers: {
                'Content-Type': file.type,
                'x-amz-tagging': uploadData.tagging,
            },
        })

        console.log('File uploaded successfully.')
    }

    async listDocumentAnalyses(
        limit: number = 4,
        offset: number = 0
    ): Promise<any> {
        console.log(
            'Listing document analyses with limit:',
            limit,
            'and offset:',
            offset
        )
        const {data} = await this.analizerApi.get('/document-analysis', {
            params: {
                limit,
                offset,
            },
        })
        console.log('Document analyses retrieved:', data)
        return data
    }

    async listAnalyzedItems(docAnalysisUuid: string): Promise<any> {
        const endpoint = `/analyzed-item/${docAnalysisUuid}/items`
        const {data} = await this.analizerApi.get(endpoint)
        return data
    }

    async downloadAnalyzedItem(
        itemUuid: string,
        fileName: string = 'documento-analisado.pdf'
    ): Promise<void> {
        const response = await this.analizerApi.get(
            `/analyzed-item/${itemUuid}/download`,
            {
                responseType: 'blob',
            }
        )

        const blob: Blob =
            response.data instanceof Blob
                ? response.data
                : new Blob([response.data], {
                      type: String(response.headers['content-type'] ?? ''),
                  })
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()

        link.parentNode?.removeChild(link)
        setTimeout(() => window.URL.revokeObjectURL(url), 1000)
    }

    async deleteDocumentAnalysis(docAnalysisUuid: string): Promise<void> {
        await this.analizerApi.delete(`/document-analysis/${docAnalysisUuid}`)
    }

    async documentsTypesIds(): Promise<string[]> {
        const {data} = await this.analizerApi.get(
            '/document-analysis/document-type-ids'
        )
        return data
    }
}
